// src/pages/Main.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Loader2, Sparkles, Gem, MessageCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import "../index.css";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import GuidedJournalChat from "../components/GuidedJournalChat";
import { RichTextEditor } from "@/components/RichTextEditor";
import { sanitizeRichHtml, sanitizeRichHtmlOrPlainText, stripHtmlToText } from "@/lib/richText";
const JOURNAL_DRAFT_KEY = "innergem_awareness_journal_draft_v1";

type Mode = "spark" | "guided";
type PlanTier =
  | "free"
  | "beginner"
  | "intermediate"
  | "upper_intermediate"
  | "lifetime_deal";

// Default limits (Free tier)
// Default limits (Free tier)
const DEFAULT_SPARK_MAX = 1000;
const DEFAULT_GUIDED_DAILY_LIMIT = 1;

// Lifetime limits
const LIFETIME_SPARK_MAX = 3000;
const LIFETIME_GUIDED_DAILY_LIMIT = 15;

export default function MainPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const PROFILE_UPDATED_EVENT = "innergem:profile-updated";

  // 文本内容 & 模式（SPARK / DEEP / GUIDED）
  const [journalText, setJournalText] = useState("");
  const [journalPlainText, setJournalPlainText] = useState("");
  const [mode, setMode] = useState<Mode>("spark");
  const [guidedContent, setGuidedContent] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // 今日 Spark / Guided 次数
  const [todaySparkCount, setTodaySparkCount] = useState(0);
  const [todayGuidedCount, setTodayGuidedCount] = useState(0);

  // 订阅 / 付费状态（Beginner / Intermediate / Upper Intermediate / Lifetime Deal）
  const [hasPaidPlan, setHasPaidPlan] = useState(false);
  const [planTier, setPlanTier] = useState<PlanTier>("free");

  // Save 弹窗
  const [isCaptureOpen, setIsCaptureOpen] = useState(false);

  // 从 DEEP -> SPARK 且字数超标时的截断确认弹窗
  const [isTrimDialogOpen, setIsTrimDialogOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<Mode | null>(null);

  // Deep / Guided 用完 3 次后的提示弹窗
  const [isDeepLimitOpen, setIsDeepLimitOpen] = useState(false);
  const [isGuidedLimitOpen, setIsGuidedLimitOpen] = useState(false);

  // Dynamic Limits
  const sparkMax = hasPaidPlan ? LIFETIME_SPARK_MAX : DEFAULT_SPARK_MAX;
  // Guided mode max: 1000 for free, 3000 for any paid plan (目前 1-4 都视为解锁)
  const guidedMax = hasPaidPlan ? 3000 : 1000;

  // Daily entry limits per tier (must stay in sync with backend generate-ai-insight function)
  const DAILY_LIMITS: Record<PlanTier, { spark: number; guided: number }> = {
    free: { spark: 1, guided: 1 },
    beginner: { spark: 3, guided: 3 },
    intermediate: { spark: 5, guided: 5 },
    upper_intermediate: { spark: 10, guided: 10 },
    lifetime_deal: { spark: 15, guided: 15 },
  };

  const sparkDailyLimit = DAILY_LIMITS[planTier].spark;
  const guidedDailyLimit = DAILY_LIMITS[planTier].guided;

  const charLimit = mode === "spark" ? sparkMax : guidedMax;

  // ---------------------------
  // Check Paid Subscription Status
  // ---------------------------
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from("user_profiles")
          .select("has_lifetime_deal, subscription_tier")
          .eq("id", user.id)
          .single();

        const paidTiers = [
          "beginner",
          "intermediate",
          "upper_intermediate",
          "lifetime_deal",
          "beginner_monthly",
          "beginner_yearly",
          "intermediate_monthly",
          "intermediate_yearly",
          "upper_intermediate_monthly",
          "upper_intermediate_yearly",
        ];

        const hasLifetimeDeal = !!data?.has_lifetime_deal;
        const rawTier = (data as any)?.subscription_tier as string | null;
        const tier = rawTier
          ? rawTier.toLowerCase().replace(/\s+/g, "_")
          : null;
        const hasPaidTier = tier ? paidTiers.includes(tier) : false;

        let nextPlan: PlanTier = "free";
        if (hasLifetimeDeal) {
          nextPlan = "lifetime_deal";
        } else if (tier) {
          if (tier.startsWith("beginner")) nextPlan = "beginner";
          else if (tier.startsWith("intermediate")) nextPlan = "intermediate";
          else if (tier.startsWith("upper_intermediate")) nextPlan = "upper_intermediate";
          else if (tier === "lifetime_deal") nextPlan = "lifetime_deal";
        }

        setPlanTier(nextPlan);
        if (hasLifetimeDeal || hasPaidTier) {
          setHasPaidPlan(true);
        } else {
          setHasPaidPlan(false);
        }
      }
    };

    const onProfileUpdated = () => {
      void checkSubscriptionStatus();
    };

    checkSubscriptionStatus();
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);

    return () => {
      window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    };
  }, []);

  // ---------------------------
  // 查询今天 Spark / Guided 使用次数（Supabase journals 表）
  // ---------------------------
  const fetchTodayCountByMode = async (
    aiMode: "spark" | "guided"
  ): Promise<number> => {
    const { data, error: authError } = await supabase.auth.getUser();
    const userId = data?.user?.id;

    if (authError || !userId) {
      console.warn("Cannot load user for limit check:", authError);
      return 0;
    }

    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();

    const startUTC = new Date(Date.UTC(y, m, d, 0, 0, 0));
    const endUTC = new Date(Date.UTC(y, m, d + 1, 0, 0, 0));

    const { count, error } = await supabase
      .from("journals")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("ai_mode", aiMode)
      .gte("created_at", startUTC.toISOString())
      .lt("created_at", endUTC.toISOString());

    if (error) {
      console.error(`Failed to fetch today ${aiMode} count`, error);
      return 0;
    }

    return count ?? 0;
  };

  // 页面加载时，抓 Spark / Guided 已用次数
  useEffect(() => {
    (async () => {
      const [spark, guided] = await Promise.all([
        fetchTodayCountByMode("spark"),
        fetchTodayCountByMode("guided"),
      ]);
      setTodaySparkCount(spark);
      setTodayGuidedCount(guided);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 页面加载时，从 localStorage 恢复上一次编辑的内容（包含当前模式）
  useEffect(() => {
    try {
      const raw = localStorage.getItem(JOURNAL_DRAFT_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);

      if (saved && typeof saved.text === "string") {
        const html = sanitizeRichHtmlOrPlainText(saved.text);
        setJournalText(html);
        setJournalPlainText(stripHtmlToText(html));
      }
      if (saved && (saved.mode === "spark" || saved.mode === "guided")) {
        setMode(saved.mode);
      }
      if (saved && typeof saved.guidedContent === "string") {
        setGuidedContent(saved.guidedContent);
      }
    } catch (e) {
      console.warn("Failed to restore journal draft:", e);
    }
  }, []);

  // 每次 mode / journalText 变化就自动保存
  useEffect(() => {
    const payload = { mode, text: journalText, guidedContent };
    localStorage.setItem(JOURNAL_DRAFT_KEY, JSON.stringify(payload));
  }, [mode, journalText, guidedContent]);


  const handleSparkEditorChange = (nextHtml: string, nextPlain: string) => {
    setJournalText(nextHtml);
    setJournalPlainText(nextPlain);
    setErrorMsg(null);
  };

  // ---------------------------
  // 只保存 Journal（SPARK / GUIDED）
  // ---------------------------
  const saveJournalOnly = async () => {
    const rawHtml = mode === "guided" ? guidedContent : journalText;
    const sanitizedHtml = sanitizeRichHtml(rawHtml);
    const plain = stripHtmlToText(sanitizedHtml).trim();

    if (!plain) {
      setErrorMsg(
        t('home.error.writeFirst', {
          defaultValue: 'Please write a little about what you are noticing today first.'
        })
      );
      return;
    }

    setErrorMsg(null);
    setIsLoading(true);

    try {
      const { data: userData, error: authError } =
        await supabase.auth.getUser();

      if (authError || !userData?.user) {
        console.warn("No user session when saving journal:", authError);
        setErrorMsg(
          t('home.error.sessionMissing', {
            defaultValue: 'Your session seems to be missing. Please go to the Login page, sign in, then come back to save your Gem.'
          })
        );
        return;
      }

      const userId = userData.user.id;

      const aiMode: "spark" | "guided" =
        mode === "spark" ? "spark" : "guided";

      const { data, error } = await supabase
        .from("journals")
        .insert({
          user_id: userId,
          content: sanitizedHtml,
          ai_mode: aiMode,
        })
        .select("id")
        .single();

      if (error || !data) {
        console.error("Insert journal error:", error);
        setErrorMsg(
          t('home.error.unableSave', {
            defaultValue: 'Unable to save your Gem. Please check your connection and try again.'
          })
        );
        return;
      }

      // 保存成功后更新计数 + 清空状态
      if (aiMode === "guided") {
        setGuidedContent("");
        setTodayGuidedCount((c) => c + 1);

        // Clear the guided chat transcript draft only after a successful save.
        localStorage.removeItem("innergem_guided_satir_draft_v2");
      } else {
        setJournalText("");
        setJournalPlainText("");
        setTodaySparkCount((c) => c + 1);
      }

      navigate(`/gem/${data.id}`);
      localStorage.removeItem(JOURNAL_DRAFT_KEY);

    } catch (err) {
      console.error("Unexpected error when saving journal:", err);
      setErrorMsg(
        t('home.error.serverProblem', {
          defaultValue: 'There was a problem talking to the server. Please try again in a moment.'
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------
  // 点击 “Save today’s Gem”
  // ---------------------------
  const handleCaptureClick = async () => {
    setErrorMsg(null);

    if (mode === "guided") {
      if (!stripHtmlToText(guidedContent).trim()) {
        setErrorMsg(
          t('home.error.guidedIncomplete', {
            defaultValue: "Write a little more in this guided walk first, then you can save your Gem."
          })
        );
        return;
      }

      const count = await fetchTodayCountByMode("guided");
      setTodayGuidedCount(count);

      if (count >= guidedDailyLimit) {
        setIsGuidedLimitOpen(true);
        return;
      }

      setIsCaptureOpen(true);
      return;
    }

    // SPARK / GUIDED：沿用原来的逻辑
    if (!journalPlainText.trim()) {
      setErrorMsg(
        t('home.error.writeFirst', {
          defaultValue: 'Please write a little about what you are noticing today first.'
        })
      );
      return;
    }

    if (mode === "spark") {
      if (journalPlainText.length > sparkMax) {
        setErrorMsg(
          t('home.error.sparkLimit', {
            count: sparkMax,
            defaultValue: 'SPARK mode is limited to {{count}} characters. Please shorten your entry a little.'
          })
        );
        return;
      }
      setIsCaptureOpen(true);
      return;
    }

    // 无 Deep 模式，直接打开保存弹窗
    setIsCaptureOpen(true);
  };

  const handleConfirmCapture = async () => {
    setIsCaptureOpen(false);
    await saveJournalOnly();
  };

  // ---------------------------
  // 模式切换
  // ---------------------------
  const handleModeChange = (nextMode: Mode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setErrorMsg(null);
  };

  const confirmTrimToSpark = () => {
    setJournalText((prev) => prev.slice(0, sparkMax));
    setMode("spark");
    setIsTrimDialogOpen(false);
    setPendingMode(null);
  };

  const cancelTrimToSpark = () => {
    setIsTrimDialogOpen(false);
    setPendingMode(null);
  };

  // ---------------------------
  // JSX
  // ---------------------------
  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      {/* 顶部标题 */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-primary mb-2">
          {t("home.title")}
        </h1>
        <p className="text-muted-foreground">{t("home.subtitle")}</p>
      </div>

      {/* 主卡片：觉察日记输入 */}
      <div className="bg-card/80 border rounded-2xl shadow-sm p-4 md:p-6 flex flex-col">
        {/* 标题 + 引导按钮 + 内在剧组按钮 */}
        <div className="mb-2 flex items-center justify-between gap-3">
          <label className="text-xs font-medium text-muted-foreground">
            {t('home.journal_label', { defaultValue: "Today's Reflection · Awareness Journal" })}
          </label>

          <div className="flex flex-wrap items-center gap-2 w-full justify-end md:w-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => navigate('/actors')}
            >
              {t('nav.innerCast', { defaultValue: 'Inner Cast' })}
            </Button>

            <Dialog>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 max-w-[140px] w-full md:w-auto whitespace-normal text-xs px-2 py-1 h-auto"
                >
                  {t("home.help_btn")}
                </Button>
              </DialogTrigger>

              <DialogContent
                className="max-w-lg w-[92vw] sm:w-full max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden rounded-2xl"
              >
              <div className="max-h-[80vh] overflow-y-auto no-scrollbar px-6 py-4">
                <DialogHeader>
                  <DialogTitle>
                    {t('home.help.title', { defaultValue: 'Why & How to Write Your Awareness Journal' })}
                  </DialogTitle>
                </DialogHeader>

                <div className="mt-3 space-y-5 text-sm text-muted-foreground">
                  {/* 一句话简介 */}
                  <p className="text-xs uppercase tracking-wide text-primary/80 font-semibold">
                    {t('home.help.tagline', { defaultValue: 'A gentle place to see yourself clearly, one small note at a time.' })}
                  </p>

                  {/* Why (Purpose) */}
                  <section className="space-y-2">
                    <h3 className="font-semibold text-foreground text-sm">
                      {t('home.help.why.title', { defaultValue: 'Why (Purpose)' })}
                    </h3>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>{t('home.help.why.items.de_stress', { defaultValue: 'De-stress: Writing slows the mind and lets emotions settle.' })}</li>
                      <li>{t('home.help.why.items.self_awareness', { defaultValue: 'Self-awareness: Name what you feel, so it stops running the show.' })}</li>
                      <li>{t('home.help.why.items.integration', { defaultValue: 'Integration: Tiny reflections today become clear directions tomorrow.' })}</li>
                      <li>{t('home.help.why.items.compassion', { defaultValue: 'Compassion: Treat yourself like a friend; the page is a safe mirror.' })}</li>
                    </ul>
                  </section>

                  {/* What (What to write) */}
                  <section className="space-y-2">
                    <h3 className="font-semibold text-foreground text-sm">
                      {t('home.help.what.title', { defaultValue: 'What (What to write)' })}
                    </h3>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>{t('home.help.what.items.examples', { defaultValue: 'A single feeling, a moment that stood out, a body signal, or a tiny next step.' })}</li>
                      <li>{t('home.help.what.items.truth', { defaultValue: 'Not perfect, just true. One honest sentence beats a polished story.' })}</li>
                    </ul>
                  </section>

                  {/* When (Best moments) */}
                  <section className="space-y-2">
                    <h3 className="font-semibold text-foreground text-sm">
                      {t('home.help.when.title', { defaultValue: 'When (Best moments)' })}
                    </h3>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>{t('home.help.when.items.micro', { defaultValue: 'Micro-moments: right after a trigger, before bed, or during a short break.' })}</li>
                      <li>{t('home.help.when.items.retro', { defaultValue: 'If you missed the moment, write retrospectively, it still counts.' })}</li>
                    </ul>
                  </section>

                  {/* Who (Who it's for) */}
                  <section className="space-y-2">
                    <h3 className="font-semibold text-foreground text-sm">
                      {t('home.help.who.title', { defaultValue: 'Who (Who it’s for)' })}
                    </h3>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>{t('home.help.who.items.private', { defaultValue: 'Written by you, for you. Private by default.' })}</li>
                      <li>{t('home.help.who.items.control', { defaultValue: 'You can optionally share a summary later; you are in control.' })}</li>
                    </ul>
                  </section>

                  {/* How (Simple flow) */}
                  <section className="space-y-2">
                    <h3 className="font-semibold text-foreground text-sm">
                      {t('home.help.how.title', { defaultValue: 'How (Simple flow)' })}
                    </h3>
                    <ul className="list-decimal pl-5 space-y-1">
                      <li>{t('home.help.how.items.notice', { defaultValue: 'Notice one thing (feeling, thought, body cue, need).' })}</li>
                      <li>{t('home.help.how.items.name', { defaultValue: 'Name it in plain words.' })}</li>
                      <li>{t('home.help.how.items.nudge', { defaultValue: 'Nudge: add one small kind action you could take next.' })}</li>
                      <li>{t('home.help.how.items.capture', { defaultValue: 'Capture it: your Gem is saved and grows over time.' })}</li>
                    </ul>
                  </section>

                  <hr className="my-2 border-border/60" />

                  {/* Modes Explanation: Spark / Deep / Guided */}
                  <section className="space-y-3">
                    <h3 className="font-semibold text-foreground text-sm">
                      {t('home.help.modes.title', { defaultValue: 'Modes: Spark, Deep & Guided' })}
                    </h3>

                    {/* Spark */}
                    <div className="space-y-1">
                      <h4 className="font-semibold text-[13px] flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-purple-500" /> {t('home.help.modes.spark.title', { defaultValue: 'Gem Spark' })}
                      </h4>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>{t('home.help.modes.spark.limit', { defaultValue: 'Limit: up to {{count}} characters.', count: sparkMax })}</li>
                        <li>{t('home.help.modes.spark.use', { defaultValue: 'Use when: you have a quick feeling or insight to capture.' })}</li>
                      </ul>
                    </div>

                    {/* Deep mode removed */}

                    {/* ✨ 新增：Guided Mode 说明 ✨ */}
                    <div className="space-y-1">
                      <h4 className="font-semibold text-[13px] flex items-center gap-2">
                        <MessageCircle className="w-3 h-3 text-purple-500" /> {t('guided.title', { defaultValue: 'Guided Satir Walk' })}
                      </h4>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>{t('home.help.modes.guided.limit', { defaultValue: 'Limit: up to {{chars}} characters, {{entries}} entries per day.', chars: guidedMax, entries: guidedDailyLimit })}</li>
                        <li>{t('home.help.modes.guided.how', { defaultValue: 'How it works: A step-by-step dialogue based on the Satir Iceberg model to help you go deeper.' })}</li>
                        <li>{t('home.help.modes.guided.remember', { defaultValue: 'Remember: The questions are randomized invitations, not exam questions. If a question doesn\'t fit, feel free to write whatever is truly alive in you. You are the director of this walk.' })}</li>
                      </ul>
                    </div>

                    <p className="text-xs italic pt-1">
                      {t('home.help.tip', { defaultValue: "Tip: Can’t decide? Start with Spark. If more wants to be said, switch to Deep or Guided." })}
                    </p>
                  </section>
                  <hr className="my-2 border-border/60" />

                  {/* What you get */}
                  <section className="space-y-2">
                    <h3 className="font-semibold text-foreground text-sm">
                      {t('home.help.features.title', { defaultValue: 'What you get (Features)' })}
                    </h3>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>{t('home.help.features.more_coming', { defaultValue: 'More features coming. Stay tuned!' })}</li>
                    </ul>
                  </section>

                  <p className="text-xs text-foreground/80">
                    {t('home.help.closing', { defaultValue: "Your words don’t have to be perfect, only honest and kind." })}
                  </p>
                </div>
              </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* ✨ 核心：根据模式渲染不同输入区 ✨ */}
        <div className="flex-1 flex flex-col">
          {mode === "guided" ? (
            <GuidedJournalChat
              maxLimit={guidedMax}
              onComplete={(text) => {
                setGuidedContent(text);
                setErrorMsg(null);
              }}
            />
          ) : (
            <div className="relative">
              <RichTextEditor
                valueHtml={journalText}
                onChange={handleSparkEditorChange}
                placeholder={t("home.placeholder") as string}
                disabled={isLoading}
                maxLength={charLimit}
              />
              <div className="pointer-events-none absolute bottom-2 right-3 text-[11px] text-muted-foreground">
                {journalPlainText.length}/{charLimit}
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {errorMsg && (
          <p className="mt-3 text-xs text-red-500 flex items-center gap-2">
            {errorMsg}
          </p>
        )}

        {/* 模式选择 + 保存按钮 */}
        <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
          {/* 左侧：模式选择 */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {t('home.choose_mode', { defaultValue: 'Choose your InnerGem mode' })}
            </span>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleModeChange("spark")}
                className={[
                  "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs",
                  "transition-colors hover:bg-primary/5",
                  mode === "spark"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground",
                ].join(" ")}
              >
                <Sparkles className="w-3 h-3" />
                {t("home.modes.spark")}
              </button>

              {/* Deep mode removed */}

              <button
                type="button"
                onClick={() => handleModeChange("guided")}
                className={[
                  "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs",
                  "transition-colors hover:bg-primary/5",
                  mode === "guided"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground",
                ].join(" ")}
              >
                <MessageCircle className="w-3 h-3" />
                {t("home.modes.guided")}
              </button>

              {/* ...existing code... (voice mode button removed) */}
            </div>

            {/* Daily usage display for Spark & Guided */}
            <div className="flex flex-col text-[11px] text-muted-foreground">
              <span>
                {t('home.usage.spark', {
                  defaultValue: 'Spark used today: {{used}}/{{limit}}',
                  used: todaySparkCount,
                  limit: sparkDailyLimit,
                })}
              </span>
              <span>
                {t('home.usage.guided', {
                  defaultValue: 'Guided used today: {{used}}/{{limit}}',
                  used: todayGuidedCount,
                  limit: guidedDailyLimit,
                })}
              </span>
            </div>
          </div>

          {/* 右侧：保存按钮 */}
          <div className="flex items-center gap-2 md:justify-end">
            <button
              type="button"
              disabled={isLoading}
              onClick={handleCaptureClick}
              className={[
                "inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium",
                "bg-primary text-primary-foreground shadow-sm",
                "disabled:opacity-60 disabled:cursor-not-allowed",
                "hover:bg-primary/90 transition-colors",
              ].join(" ")}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("home.saving")}
                </>
              ) : (
                <>
                  <Gem className="w-4 h-4 mr-2" />
                  {t("home.save_button")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Save Your Gem? 弹窗 */}
      <Dialog open={isCaptureOpen} onOpenChange={setIsCaptureOpen}>
        <DialogContent className="sm:max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-center">
              {t('home.save.confirmTitle', { defaultValue: 'Save Your Gem?' })}
            </DialogTitle>
            <DialogDescription>
              {t('home.save.confirmDesc', { defaultValue: "We'll gently save today's reflection into your Gems archive." })}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-6 flex justify-center gap-3 sm:justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCaptureOpen(false)}
            >
              {t('home.save.notYet', { defaultValue: 'Not yet' })}
            </Button>
            <Button type="button" onClick={handleConfirmCapture}>
              {t('home.save.yes', { defaultValue: 'Yes, save my Gem' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 从 DEEP 切到 SPARK 时的截断确认弹窗 */}
      <Dialog open={isTrimDialogOpen} onOpenChange={setIsTrimDialogOpen}>
        <DialogContent className="sm:max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center">
              {t('home.trim.title', { defaultValue: 'Switch to SPARK?' })}
            </DialogTitle>
            <DialogDescription className="mt-10 text-sm leading-relaxed text-center">
              {t('home.trim.desc', { defaultValue: 'Your entry is longer than {{count}} characters. To use SPARK, we\'ll gently trim the extra text.', count: sparkMax })}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-4 flex justify-center gap-7">
            <Button
              type="button"
              variant="outline"
              onClick={cancelTrimToSpark}
              className="min-w-[200px]"
            >
              {t('home.trim.rewrite', { defaultValue: "I'll rewrite it myself" })}
            </Button>
            <Button
              type="button"
              onClick={confirmTrimToSpark}
              className="min-w-[200px]"
            >
              {t('home.trim.trimSwitch', { defaultValue: 'Trim & switch to SPARK' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deep 每日 3 次上限提示弹窗 */}
      <Dialog open={isDeepLimitOpen} onOpenChange={setIsDeepLimitOpen}>
        <DialogContent className="sm:max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center">
              {t('home.limit.deep.title', { defaultValue: 'Deep Gems for today are full ✨' })}
            </DialogTitle>

            <DialogDescription className="mt-10 text-sm leading-relaxed text-center">
              {t('home.limit.deep.desc', { defaultValue: "It seems today's deep reflections need a little more time to settle.\nLet's continue our deep exploration again tomorrow. 💜" })}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-5 flex justify-center">
            <Button
              type="button"
              onClick={() => setIsDeepLimitOpen(false)}
            >
              {t('home.limit.ok', { defaultValue: "Okay, I'll come back tomorrow" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guided 每日 3 次上限提示弹窗 */}
      <Dialog open={isGuidedLimitOpen} onOpenChange={setIsGuidedLimitOpen}>
        <DialogContent className="sm:max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center">
              {t('home.limit.guided.title', { defaultValue: 'Guided walks for today are full ✨' })}
            </DialogTitle>

            <DialogDescription className="mt-10 text-sm leading-relaxed text-center">
              {t('guided.dailyLimitReached', { defaultValue: "You've already completed {{count}} deep Guided Satir walks", count: guidedDailyLimit })}
              {t('home.limit.guided.desc', { defaultValue: 'today.\nLet your insights settle, and come back for another walk tomorrow. 💜' })}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-5 flex justify-center">
            <Button
              type="button"
              onClick={() => setIsGuidedLimitOpen(false)}
            >
              {t('home.limit.ok', { defaultValue: "Okay, I'll come back tomorrow" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
